import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { Package, Search, X } from "lucide-react";

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnOrderId, setReturnOrderId] = useState(null);
  const [returnItemId, setReturnItemId] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [returnAction, setReturnAction] = useState("refund");
  const [returnDetails, setReturnDetails] = useState("");
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [returnError, setReturnError] = useState("");

  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    document.body.style.fontFamily = "'Roboto', sans-serif";
    if (!token) {
      window.location.href = "/login";
      return;
    }
    console.log("JWT Token:", token);
    fetchMyOrders();
  }, [token]);

  const fetchMyOrders = () => {
    setLoading(true);
    setError("");
    fetch("/api/orders/user/", {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((r) => {
        if (!r.ok) return r.json().then((err) => { throw err; });
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data)) setOrders(data);
        else setOrders([]);
      })
      .catch((err) => {
        console.error(err);
        setError(err.detail || "Failed to load orders");
      })
      .finally(() => setLoading(false));
  };

  const fetchOrderDetails = (orderId) => {
    setDetailsLoading(true);
    fetch(`/api/orders/${orderId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((r) => {
        if (!r.ok) return r.json().then((err) => { throw err; });
        return r.json();
      })
      .then((data) => {
        setOrderDetails(data);
        setSelectedOrder(orderId);
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to load order details");
      })
      .finally(() => setDetailsLoading(false));
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setOrderDetails(null);
  };

  const filteredOrders = Array.isArray(orders)
    ? orders.filter((order) => {
        const matchesSearch = order.order_number.toLowerCase().includes(search.toLowerCase());
        const matchesFilter = statusFilter === "all" || order.status === statusFilter;
        return matchesSearch && matchesFilter;
      })
    : [];

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-gray-100 text-gray-700",
      processing: "bg-yellow-100 text-yellow-700",
      shipped: "bg-blue-100 text-blue-700",
      delivered: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return `px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${styles[status] || styles.pending}`;
  };

  const getReturnStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-600"}`;
  };

  const handleReturnSubmit = async () => {
    setReturnSubmitting(true);
    setReturnError("");

    try {
      const res = await fetch(
        `/api/orders/${returnOrderId}/items/${returnItemId}/return/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason: returnReason,
            action_requested: returnAction,
            details: returnDetails,
          }),
        }
      );

      const text = await res.text();
      if (!res.ok) throw new Error(text || "Failed to submit return");

      alert("Return request submitted!");
      setShowReturnModal(false);
      setReturnReason("");
      setReturnAction("refund");
      setReturnDetails("");
      setReturnItemId(null);
      fetchOrderDetails(returnOrderId);
    } catch (err) {
      setReturnError(err.message);
    } finally {
      setReturnSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-lg">Loading...</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-red-600 text-lg">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
              <Package className="w-7 h-7 text-blue-600" />
              My Orders ({orders.length})
            </h1>
            <button onClick={fetchMyOrders} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Refresh
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-700"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-700"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm sm:text-base">
              <thead className="bg-gray-100 border-b text-gray-700">
                <tr>
                  <th className="text-left p-3 sm:p-4">Order #</th>
                  <th className="text-left p-3 sm:p-4">Total</th>
                  <th className="text-left p-3 sm:p-4">Status</th>
                  <th className="text-left p-3 sm:p-4">Date</th>
                  <th className="text-left p-3 sm:p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const hasReturn = order.items?.some(i => i.return_requests?.length > 0);
                  return (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 sm:p-4 font-mono text-xs sm:text-sm text-gray-700">{order.order_number}</td>
                      <td className="p-3 sm:p-4 font-medium text-gray-800">₹{order.grand_total}</td>
                      <td className="p-3 sm:p-4">
                        <span className={getStatusBadge(order.status)}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                        {hasReturn && <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">Partial Return</span>}
                      </td>
                      <td className="p-3 sm:p-4 text-gray-600">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td className="p-3 sm:p-4 flex gap-2 flex-wrap">
                        <button onClick={() => fetchOrderDetails(order.id)} className="text-blue-600 hover:underline text-sm">View Details</button>
                        <a href={`/invoice/${order.id}`} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:underline text-sm">Invoice</a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No orders found</p>
            </div>
          )}
        </div>
      </div>

      {/* ORDER DETAILS MODAL */}
      {selectedOrder && orderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
            <button onClick={closeModal} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold mb-4 text-gray-800">Order #{orderDetails.order_number}</h2>

            <div className="space-y-2 mb-4 text-gray-700">
              <p>Status: <span className={getStatusBadge(orderDetails.status)}>{orderDetails.status}</span></p>
              <p>Placed on: {new Date(orderDetails.created_at).toLocaleDateString()}</p>
              {orderDetails.estimated_delivery && <p>Estimated Delivery: {orderDetails.estimated_delivery}</p>}
            </div>

            <h3 className="text-lg font-semibold mb-2 text-gray-800">Items:</h3>
            <div className="border rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="p-2 text-left">Product</th>
                    <th className="p-2 text-left">Color</th>
                    <th className="p-2 text-left">Size</th>
                    <th className="p-2 text-left">Qty</th>
                    <th className="p-2 text-left">Price</th>
                    <th className="p-2 text-left">Return</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.items.map((item) => {
                    const hasReturn = item.return_requests?.length > 0;
                    const returnStatus = hasReturn ? item.return_requests[0].status : null;
                    return (
                      <tr key={item.id} className="border-t text-gray-700">
                        <td className="p-2 flex items-center gap-2">
                          <img src={item.product_image} alt={item.product_name} className="w-10 h-10 rounded object-cover border" />
                          {item.product_name}
                        </td>
                        <td className="p-2">{item.color || "-"}</td>
                        <td className="p-2">{item.size || "-"}</td>
                        <td className="p-2">{item.quantity}</td>
                        <td className="p-2">₹{item.price}</td>
                        <td className="p-2">
                          {orderDetails.status === "delivered" && !hasReturn ? (
                            <button
                              onClick={() => {
                                setReturnOrderId(orderDetails.id);
                                setReturnItemId(item.id);
                                setShowReturnModal(true);
                              }}
                              className="text-red-600 hover:underline text-sm font-medium"
                            >
                              Return
                            </button>
                          ) : hasReturn ? (
                            <span className={getReturnStatusBadge(returnStatus)}>{returnStatus}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Not eligible</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl mb-6 border border-gray-200">
              <h4 className="font-semibold text-lg mb-3 text-gray-800">Delivery Address</h4>
              {typeof orderDetails.address === "object" ? (
                <div className="text-gray-700 text-sm space-y-1">
                  <p><strong>Name:</strong> {orderDetails.address.name}</p>
                  <p><strong>Phone:</strong> {orderDetails.address.phone}</p>
                  <p><strong>Address:</strong> {orderDetails.address.house}, {orderDetails.address.road}</p>
                  <p><strong>City:</strong> {orderDetails.address.city}, {orderDetails.address.state} - {orderDetails.address.pincode}</p>
                  <p><strong>Nearby:</strong> {orderDetails.address.nearby}</p>
                  {orderDetails.address.instructions && <p><strong>Instructions:</strong> {orderDetails.address.instructions}</p>}
                  <p><strong>Type:</strong> {orderDetails.address.type}</p>
                  <p><strong>Weekend:</strong> {
                    orderDetails.address.weekend_saturday && orderDetails.address.weekend_sunday ? "Sat & Sun" :
                    orderDetails.address.weekend_sunday ? "Sunday" :
                    orderDetails.address.weekend_saturday ? "Saturday" : "No"
                  }</p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">{orderDetails.address}</p>
              )}
            </div>

            <div className="text-right space-y-1 text-gray-800">
              <p>Total: ₹{orderDetails.total}</p>
              {orderDetails.discount_amount > 0 && <p>Discount: -₹{orderDetails.discount_amount}</p>}
              <p>Delivery Fee: ₹{orderDetails.delivery_fee}</p>
              <p className="font-bold text-lg">Grand Total: ₹{orderDetails.grand_total}</p>
            </div>
          </div>
        </div>
      )}

      {/* RETURN MODAL */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Return Item</h3>
            {returnError && <p className="text-red-600 text-sm bg-red-50 p-2 rounded mb-3">{returnError}</p>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Reason</label>
                <select value={returnReason} onChange={(e) => setReturnReason(e.target.value)} className="w-full p-2 border rounded-lg">
                  <option value="">Select reason</option>
                  <option value="defective">Defective</option>
                  <option value="wrong_item">Wrong Item</option>
                  <option value="size_issue">Size Issue</option>
                  <option value="not_liked">Not Liked</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Action</label>
                <div className="flex gap-4">
                  <label className="flex items-center"><input type="radio" name="action" value="refund" checked={returnAction === "refund"} onChange={(e) => setReturnAction(e.target.value)} className="mr-2" /> Refund</label>
                  <label className="flex items-center"><input type="radio" name="action" value="replace" checked={returnAction === "replace"} onChange={(e) => setReturnAction(e.target.value)} className="mr-2" /> Replace</label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Details (Optional)</label>
                <textarea value={returnDetails} onChange={(e) => setReturnDetails(e.target.value)} rows="3" className="w-full p-2 border rounded-lg" placeholder="Explain..." />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowReturnModal(false); setReturnError(""); setReturnItemId(null); }} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleReturnSubmit} disabled={returnSubmitting || !returnReason} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {returnSubmitting ? "Submitting..." : "Submit Return"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-lg text-gray-700"><p>Loading details...</p></div>
        </div>
      )}
    </div>
  );
}